import { useMemo, useState, useEffect } from "react";

type Country = { code: string; name: string; dial: string; flag: string };

// Common countries first, then a wide international list.
export const COUNTRIES: Country[] = [
  { code: "MA", name: "Morocco", dial: "+212", flag: "🇲🇦" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { code: "ES", name: "Spain", dial: "+34", flag: "🇪🇸" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "DE", name: "Germany", dial: "+49", flag: "🇩🇪" },
  { code: "IT", name: "Italy", dial: "+39", flag: "🇮🇹" },
  { code: "NL", name: "Netherlands", dial: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgium", dial: "+32", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", dial: "+41", flag: "🇨🇭" },
  { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹" },
  { code: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪" },
  { code: "SE", name: "Sweden", dial: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norway", dial: "+47", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", dial: "+45", flag: "🇩🇰" },
  { code: "FI", name: "Finland", dial: "+358", flag: "🇫🇮" },
  { code: "PL", name: "Poland", dial: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Czechia", dial: "+420", flag: "🇨🇿" },
  { code: "AT", name: "Austria", dial: "+43", flag: "🇦🇹" },
  { code: "RU", name: "Russia", dial: "+7", flag: "🇷🇺" },
  { code: "TR", name: "Turkey", dial: "+90", flag: "🇹🇷" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
  { code: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽" },
  { code: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿" },
  { code: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷" },
  { code: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
  { code: "HK", name: "Hong Kong", dial: "+852", flag: "🇭🇰" },
  { code: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
  { code: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾" },
  { code: "TH", name: "Thailand", dial: "+66", flag: "🇹🇭" },
  { code: "ID", name: "Indonesia", dial: "+62", flag: "🇮🇩" },
  { code: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
  { code: "PK", name: "Pakistan", dial: "+92", flag: "🇵🇰" },
  { code: "AE", name: "UAE", dial: "+971", flag: "🇦🇪" },
  { code: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
  { code: "QA", name: "Qatar", dial: "+974", flag: "🇶🇦" },
  { code: "KW", name: "Kuwait", dial: "+965", flag: "🇰🇼" },
  { code: "BH", name: "Bahrain", dial: "+973", flag: "🇧🇭" },
  { code: "OM", name: "Oman", dial: "+968", flag: "🇴🇲" },
  { code: "JO", name: "Jordan", dial: "+962", flag: "🇯🇴" },
  { code: "LB", name: "Lebanon", dial: "+961", flag: "🇱🇧" },
  { code: "EG", name: "Egypt", dial: "+20", flag: "🇪🇬" },
  { code: "DZ", name: "Algeria", dial: "+213", flag: "🇩🇿" },
  { code: "TN", name: "Tunisia", dial: "+216", flag: "🇹🇳" },
  { code: "LY", name: "Libya", dial: "+218", flag: "🇱🇾" },
  { code: "SN", name: "Senegal", dial: "+221", flag: "🇸🇳" },
  { code: "CI", name: "Côte d'Ivoire", dial: "+225", flag: "🇨🇮" },
  { code: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦" },
  { code: "NG", name: "Nigeria", dial: "+234", flag: "🇳🇬" },
  { code: "KE", name: "Kenya", dial: "+254", flag: "🇰🇪" },
  { code: "IL", name: "Israel", dial: "+972", flag: "🇮🇱" },
  { code: "GR", name: "Greece", dial: "+30", flag: "🇬🇷" },
  { code: "RO", name: "Romania", dial: "+40", flag: "🇷🇴" },
  { code: "HU", name: "Hungary", dial: "+36", flag: "🇭🇺" },
  { code: "UA", name: "Ukraine", dial: "+380", flag: "🇺🇦" },
];

type Props = {
  value: string;
  onChange: (full: string) => void;
  error?: string;
  label?: string;
};

export function PhoneField({ value, onChange, error, label = "Phone" }: Props) {
  // Parse incoming value into dial + local
  const initial = useMemo(() => {
    const trimmed = (value ?? "").trim();
    const match = COUNTRIES.find((c) => trimmed.startsWith(c.dial));
    if (match) {
      return { country: match, local: trimmed.slice(match.dial.length).trim() };
    }
    return { country: COUNTRIES[0], local: trimmed.replace(/^\+/, "") };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [country, setCountry] = useState<Country>(initial.country);
  const [local, setLocal] = useState<string>(initial.local);
  const [userPicked, setUserPicked] = useState<boolean>(!!value);

  // Auto-detect country from geo when user hasn't picked one and no incoming value
  useEffect(() => {
    if (userPicked) return;
    let cancelled = false;
    import("@/lib/geo").then(({ detectGeo }) => {
      detectGeo().then((g) => {
        if (cancelled) return;
        const match = COUNTRIES.find((c) => c.code === g.countryCode);
        if (match) setCountry(match);
      }).catch(() => { /* ignore */ });
    });
    return () => { cancelled = true; };
  }, [userPicked]);

  useEffect(() => {
    const cleaned = local.replace(/[^0-9 \-]/g, "").trim();
    onChange(cleaned ? `${country.dial} ${cleaned}` : "");
  }, [country, local]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">
        {label}
      </label>
      <div className="flex gap-2">
        <div className="relative">
          <select
            value={country.code}
            onChange={(e) => {
              const next = COUNTRIES.find((c) => c.code === e.target.value);
              if (next) { setCountry(next); setUserPicked(true); }
            }}
            className="h-11 appearance-none rounded-md border border-border bg-input pl-3 pr-8 text-sm focus:border-primary focus:outline-none"
            aria-label="Country code"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.dial}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
            ▾
          </div>
        </div>
        <input
          type="tel"
          inputMode="tel"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="6 12 34 56 78"
          className="h-11 flex-1 rounded-md border border-border bg-input px-3 text-sm focus:border-primary focus:outline-none"
        />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
